import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Grid2,
  styled,
  Stack,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { parseISO } from 'date-fns';
import { theme } from '~/shared/styles/theme';
import { ROUTES } from '~/shared/constants';
import FormTextField from '~/shared/components/FormTextField';
import FormSelect from '~/shared/components/FormSelect';
import FormDatePicker from '~/shared/components/FormDatePicker';
import ProfileImageUpload from '~/shared/components/ProfileImageUpload';
import { useOnboardingData } from '../hooks/useOnboardingData';
import { useFamilyTreeMutations } from '../../familyTree/hooks/useFamilyTreeMutations';
import {
  normalizeGeneticSex,
  toGeneticSexFormValue,
} from '../../familyTree/utils/geneticSex';
import { geneticSexOptions } from '../../familyTree/components/ModifyRelativeModal/constants';
import type { GeneticSexFormValue } from '../../familyTree/utils/geneticSex';
import useToast from '~/shared/hooks/useToast';
import Toast from '~/shared/components/Toast';
import type { EditPersonInput } from '~/graphql/gen/graphql';
import { useAcceptedInvitationContext } from '~/modules/invitation/hooks/useAcceptedInvitationContext';
import WelcomeModal from '~/modules/invitation/components/WelcomeModal';

interface OnboardingFormValues {
  firstName: string;
  lastName: string;
  geneticSex: GeneticSexFormValue | undefined;
  birthDate: Date | null;
  profileImageUrl: string | null;
}

export const OnboardingForm = () => {
  const navigate = useNavigate();
  const { personData, loading: onboardingDataLoading } = useOnboardingData();
  const { editPerson, loading } = useFamilyTreeMutations();
  const { toast, showToast, hideToast } = useToast();

  // Check if user is an EDITOR (accepted invitation) to customize the form
  const {
    isLoading: invitationContextLoading,
    isEditor,
    treeLastName,
    treeOwnerName,
    treeOwnerProfileImage,
    personData: editorPersonData,
  } = useAcceptedInvitationContext();

  const [welcomeModalOpen, setWelcomeModalOpen] = useState(isEditor);

  // Determine which person data to use: editor's person data or standard
  // onboarding data
  const currentPersonData = useMemo(() => {
    if (isEditor && editorPersonData) {
      return editorPersonData;
    }
    return personData;
  }, [isEditor, editorPersonData, personData]);

  // Compute form default values based on editor person data if available
  const formDefaultValues = useMemo<OnboardingFormValues>(() => {
    if (isEditor && editorPersonData) {
      return {
        firstName: editorPersonData.firstName ?? '',
        lastName: editorPersonData.lastName ?? '',
        geneticSex: editorPersonData.geneticSex
          ? toGeneticSexFormValue(editorPersonData.geneticSex)
          : undefined,
        birthDate: editorPersonData.birthDate
          ? parseISO(editorPersonData.birthDate)
          : null,
        profileImageUrl: editorPersonData.profileImageUrl ?? null,
      };
    }

    return {
      firstName: '',
      lastName: '',
      geneticSex: undefined,
      birthDate: null,
      profileImageUrl: null,
    };
  }, [isEditor, editorPersonData]);

  const {
    control,
    handleSubmit,
    formState: { isValid },
    setValue,
    watch,
  } = useForm<OnboardingFormValues>({
    mode: 'onChange',
    defaultValues: formDefaultValues,
  });

  const profileImageUrl = watch('profileImageUrl');

  const onSubmit = async (data: OnboardingFormValues) => {
    if (!currentPersonData?.personId) {
      showToast('Person data not found. Please try again.', 'error');
      return;
    }

    try {
      const input: EditPersonInput = {
        personId: currentPersonData.personId,
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        geneticSex: data.geneticSex
          ? normalizeGeneticSex(data.geneticSex)
          : undefined,
        birthDate: data.birthDate ? data.birthDate.toISOString() : undefined,
        profileImageUrl: data.profileImageUrl || undefined,
      };

      await editPerson(input);

      navigate(ROUTES.TREE);
    } catch (error) {
      showToast('Failed to update profile. Please try again.', 'error');
    }
  };

  const handleImageChange = useCallback(
    (url: string | null) => {
      setValue('profileImageUrl', url, { shouldValidate: true });
    },
    [setValue],
  );

  const handleWelcomeModalContinue = useCallback(() => {
    setWelcomeModalOpen(false);
  }, []);

  const isSubmitting = loading.editPerson;
  const isFormDisabled =
    onboardingDataLoading || invitationContextLoading || isSubmitting;

  const formTitle = isEditor
    ? 'Confirm Your Information'
    : 'Start Your Family Tree';
  const formSubtitle = isEditor
    ? `You've been invited to join the private ${treeLastName} family tree. Confirm or update your info and contribute to your family record.`
    : 'Build your own private family tree. Information added is secure and only accessible to invited members. You can update the details at any time.';
  const submitButtonText = 'Add Myself';

  if (onboardingDataLoading || invitationContextLoading) {
    return (
      <Container>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container>
        <CardContainer>
          <Title variant="h4">{formTitle}</Title>
          <Subtitle variant="body1">{formSubtitle}</Subtitle>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack>
              <ProfileImageUploadContainer>
                <ProfileImageUpload
                  value={profileImageUrl}
                  onChange={handleImageChange}
                />
              </ProfileImageUploadContainer>

              <FirstNameLastNameContainer container spacing={2}>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <FormTextField
                    name="firstName"
                    control={control}
                    label="First Name"
                    placeholder="Enter first name"
                    required
                    disabled={isFormDisabled}
                    rules={{
                      required: 'First name is required',
                      validate: (value) => {
                        if (typeof value === 'string' && !value.trim()) {
                          return 'First name cannot be empty or whitespace only';
                        }

                        return true;
                      },
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <FormTextField
                    name="lastName"
                    control={control}
                    label="Last Name"
                    placeholder="Enter last name"
                    required
                    disabled={isFormDisabled}
                    rules={{
                      required: 'Last name is required',
                      validate: (value) => {
                        if (typeof value === 'string' && !value.trim()) {
                          return 'Last name cannot be empty or whitespace only';
                        }

                        return true;
                      },
                    }}
                  />
                </Grid2>
              </FirstNameLastNameContainer>

              <GeneticSexBirthDateContainer container spacing={2}>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <FormSelect
                    name="geneticSex"
                    control={control}
                    label="Genetic Sex"
                    placeholder="Select genetic sex"
                    options={geneticSexOptions}
                    required
                    disabled={isFormDisabled}
                    rules={{
                      required: 'Genetic sex is required',
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <FormDatePicker
                    name="birthDate"
                    control={control}
                    label="Birthday"
                    placeholder="Select birthday"
                    required={false}
                    backgroundColor={theme.palette.common.white}
                  />
                </Grid2>
              </GeneticSexBirthDateContainer>

              <SubmitButton
                variant="contained"
                fullWidth
                disabled={!isValid || isSubmitting}
                type="submit"
              >
                {isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  submitButtonText
                )}
              </SubmitButton>
            </Stack>
          </form>
        </CardContainer>

        <Toast
          open={toast.open}
          message={toast.message}
          severity={toast.severity}
          onClose={hideToast}
        />

        <WelcomeModal
          open={welcomeModalOpen}
          treeOwnerName={treeOwnerName}
          treeOwnerProfileImage={treeOwnerProfileImage}
          onContinue={handleWelcomeModalContinue}
          onClose={handleWelcomeModalContinue}
        />
      </Container>
    </LocalizationProvider>
  );
};

const Container = styled(Stack)({
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100%',
  backgroundImage: theme.palette.custom.backgroundGradient,
  padding: theme.spacing(4),
  position: 'relative',
});

const ProfileImageUploadContainer = styled(Box)({
  marginBottom: '25px',
});

const FirstNameLastNameContainer = styled(Grid2)({
  marginBottom: '47px',
});

const GeneticSexBirthDateContainer = styled(Grid2)({
  marginBottom: '29px',
});

const CardContainer = styled(Box)({
  backgroundColor: theme.palette.custom.offWhite,
  borderRadius: '10px',
  padding: '46px 45px 58px 45px',
  maxWidth: '818px',
  width: '100%',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',

  // Style all labels within form field Stacks (FormTextField, FormSelect,
  // FormDatePicker)
  '& .MuiStack-root > .MuiTypography-root': {
    fontSize: '12px',
    fontWeight: 700,
    color: theme.palette.custom.darkBrown,
    marginBottom: '10px',
  },

  // Style the ProfileImageUpload label specifically (first Typography child)
  '& form > div > div:first-of-type > .MuiTypography-root:first-of-type': {
    fontSize: '12px',
    fontWeight: 700,
    color: theme.palette.custom.darkBrown,
    marginBottom: '12px',
  },

  // Style the "or" and "Drag and drop" text in ProfileImageUpload (body2
  // Typography inside upload area)
  '& form > div > div:first-of-type .MuiTypography-body2': {
    fontSize: '10px',
    fontWeight: 500,
    color: theme.palette.custom.textMain,
  },

  // Set white background for all input fields (TextField and Select)
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.common.white,
  },
});

const Title = styled(Typography)({
  fontSize: '30px',
  fontWeight: 500,
  color: theme.palette.custom.textTitle,
  textAlign: 'center',
  marginBottom: '15px',
  lineHeight: 1.2,
});

const Subtitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: theme.palette.custom.textSupporting,
  textAlign: 'center',
  lineHeight: 1.6,
  maxWidth: '580px',
  margin: '0 auto 28px auto',
});

const SubmitButton = styled(Button)({
  height: '56px',
  borderRadius: '28px',
  backgroundColor: theme.palette.custom.richBrown,
  color: theme.palette.common.white,
  fontSize: '18px',
  fontWeight: 600,
  textTransform: 'none',
  marginTop: 0,
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: theme.palette.custom.darkBrown,
    boxShadow: 'none',
  },
  '&:disabled': {
    backgroundColor: theme.palette.custom.beige,
    color: theme.palette.common.white,
    opacity: 1,
  },
});
